import React, {useEffect, useState} from "react";
import {useHistory} from 'react-router-dom';
import {useForm} from 'react-hook-form'
import {
    Box,
    Button,
    Flex,
    FormControl, FormErrorMessage,
    FormLabel,
    Input,
    Text,
    useColorModeValue, useToast, Checkbox, Icon, Heading
} from "@chakra-ui/react";
import Card from "../../../components/card/Card";
import {getData, putData} from "../../../services/api";
import EuropeanCountriesSelect from "../../../components/select/EuropeanCountriesSelect";

export default function UserProfile() {

    const textColor = useColorModeValue("navy.700", "white");
    const brandStars = useColorModeValue("brand.500", "brand.400");
    const toast = useToast()
    const history = useHistory();

    const [user, setUser] = useState(null);

    const {
        handleSubmit,
        register,
        reset,
        watch,
        formState: {errors, isSubmitting},
    } = useForm({
        defaultValues: {
            has_invoice_address: true,
        },
    })

    useEffect(() => {
        // Define an async function to fetch data
        const fetchData = async () => {
            try {
                const response = await getData(`/api/user`);
                setUser(response)
                reset({
                    ...response,
                    has_invoice_address: response.has_invoice_address, // Convert 1 to true and 0 to false
                })
            } catch (error) {
                toast({
                    title: 'Es ist ein Fehler aufgetreten:' + error.message,
                    status: 'error',
                    isClosable: true,
                })
            }
        };

        // Call the async function
        fetchData();
    }, []);

    function onSubmit(values) {
        return new Promise(async (resolve) => {
            try {
                let response = await putData('/api/users/' + user.id, values);

                toast({
                    title: 'Daten wurden aktualisiert',
                    status: 'success',
                    isClosable: true,
                    duration: 3000,
                })
                history.push(`/user/dashboard`);
                resolve();
            } catch (error) {
                toast({
                    title: 'Es ist ein Fehler aufgetreten:' + error.message,
                    status: 'error',
                    isClosable: true,
                })
                resolve();
            }
        })
    }

    return (
        <Box pt={"80px"}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Card mb={"20px"}>
                    <Heading color={textColor} fontSize='36px' mb='10px'>
                        Basisdaten
                    </Heading>
                    <Flex
                        zIndex='2'
                        direction='column'
                        w={{base: "100%", md: "820px"}}
                        maxW='100%'
                        background='transparent'
                        borderRadius='15px'
                        mx={{base: "auto", lg: "unset"}}
                        me='auto'
                        mb={{base: "20px", md: "auto"}}>
                        <Flex justifyContent='space-between' gap='15px' align='center' mb='24px'
                              direction={{base: "column", lg: 'row'}}>
                            <FormControl>
                                <FormLabel
                                    display='flex'
                                    ms='4px'
                                    fontSize='sm'
                                    fontWeight='500'
                                    color={textColor}
                                    htmlFor='firstname'
                                >
                                    Vorname<Text color={brandStars}>*</Text>
                                </FormLabel>
                                <Input
                                    id='firstname'
                                    isRequired={true}
                                    variant='auth'
                                    fontSize='sm'
                                    ms={{base: "0px", md: "0px"}}
                                    type='text'
                                    fontWeight='500'
                                    size='lg'
                                    disabled
                                    {...register('firstname', {
                                        required: 'Bitte geben Sie den Vornamen an',
                                    })}
                                />
                                <FormErrorMessage>
                                    {errors.firstname && errors.firstname.message}
                                </FormErrorMessage>
                            </FormControl>
                            <FormControl>
                                <FormLabel
                                    display='flex'
                                    ms='4px'
                                    fontSize='sm'
                                    fontWeight='500'
                                    color={textColor}
                                    htmlFor='lastname'
                                >
                                    Nachname<Text color={brandStars}>*</Text>
                                </FormLabel>
                                <Input
                                    id='lastname'
                                    isRequired={true}
                                    variant='auth'
                                    fontSize='sm'
                                    ms={{base: "0px", md: "0px"}}
                                    type='text'
                                    fontWeight='500'
                                    size='lg'
                                    disabled
                                    {...register('lastname', {
                                        required: 'Bitte geben Sie den Nachnamen an',
                                    })}
                                />
                                <FormErrorMessage>
                                    {errors.lastname && errors.lastname.message}
                                </FormErrorMessage>
                            </FormControl>
                        </Flex>

                        <FormControl mb="24px">
                            <FormLabel
                                htmlFor='email'
                                display='flex'
                                ms='4px'
                                fontSize='sm'
                                fontWeight='500'
                                color={textColor}
                            >
                                Email<Text color={brandStars}>*</Text>
                            </FormLabel>
                            <Input
                                id='email'
                                isRequired={true}
                                variant='auth'
                                fontSize='sm'
                                ms={{base: "0px", md: "0px"}}
                                type='text'
                                fontWeight='500'
                                size='lg'
                                disabled
                                {...register('email', {
                                    required: 'Bitte geben Sie eine Email an',
                                })}
                            />
                        </FormControl>

                        <FormControl mb="24px">
                            <FormLabel
                                htmlFor='telephone'
                                display='flex'
                                ms='4px'
                                fontSize='sm'
                                fontWeight='500'
                                color={textColor}
                            >
                                Telefon<Text color={brandStars}>*</Text>
                            </FormLabel>
                            <Input
                                id='telephone'
                                isRequired={true}
                                variant='auth'
                                fontSize='sm'
                                ms={{base: "0px", md: "0px"}}
                                type='text'
                                fontWeight='500'
                                size='lg'
                                {...register('telephone', {
                                    required: 'Bitte geben Sie eine Telefonnummer an',
                                })}
                            />
                        </FormControl>
                    </Flex>
                </Card>
                <Card mb={"20px"}>
                    <Heading color={textColor} fontSize='36px' mb='10px'>
                        Kontaktadresse
                    </Heading>
                    <FormControl mb='24px'>
                        <FormLabel
                            display='flex'
                            ms='4px'
                            fontSize='sm'
                            fontWeight='500'
                            color={textColor}
                            htmlFor='company'
                        >
                            Firma
                        </FormLabel>
                        <Input
                            id='company'
                            variant='auth'
                            fontSize='sm'
                            ms={{base: "0px", md: "0px"}}
                            type='text'
                            fontWeight='500'
                            size='lg'
                            {...register('company')}
                        />
                    </FormControl>
                    <Flex justifyContent='space-between' gap='15px' align='center' mb='24px'>
                        <FormControl>
                            <FormLabel
                                display='flex'
                                ms='4px'
                                fontSize='sm'
                                fontWeight='500'
                                color={textColor}
                                htmlFor='street'
                            >
                                Strasse<Text color={brandStars}>*</Text>
                            </FormLabel>
                            <Input
                                id='street'
                                isRequired={true}
                                variant='auth'
                                fontSize='sm'
                                ms={{base: "0px", md: "0px"}}
                                type='text'
                                fontWeight='500'
                                size='lg'
                                {...register('street', {
                                    required: 'Bitte geben Sie eine Strasse an',
                                })}
                            />
                        </FormControl>
                        <FormControl width='25%'>
                            <FormLabel
                                display='flex'
                                ms='4px'
                                fontSize='sm'
                                fontWeight='500'
                                color={textColor}
                                htmlFor='street_nr'
                            >
                                Nummer<Text color={brandStars}>*</Text>
                            </FormLabel>
                            <Input
                                id='street_nr'
                                isRequired={true}
                                variant='auth'
                                fontSize='sm'
                                ms={{base: "0px", md: "0px"}}
                                type='text'
                                fontWeight='500'
                                size='lg'
                                {...register('street_nr', {
                                    required: 'Bitte geben Sie eine Strassennummer an',
                                })}
                            />
                        </FormControl>
                    </Flex>
                    <Flex justifyContent='space-between' gap='15px' align='center' mb='24px'>
                        <FormControl width='25%'>
                            <FormLabel
                                display='flex'
                                ms='4px'
                                fontSize='sm'
                                fontWeight='500'
                                color={textColor}
                                htmlFor='zip'
                            >
                                Postleitzahl<Text color={brandStars}>*</Text>
                            </FormLabel>
                            <Input
                                id='zip'
                                isRequired={true}
                                variant='auth'
                                fontSize='sm'
                                ms={{base: "0px", md: "0px"}}
                                type='text'
                                fontWeight='500'
                                size='lg'
                                {...register('zip', {
                                    required: 'Bitte geben Sie eine Postleitzahl an',
                                })}
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel
                                display='flex'
                                ms='4px'
                                fontSize='sm'
                                fontWeight='500'
                                color={textColor}
                                htmlFor='place'
                            >
                                Ort<Text color={brandStars}>*</Text>
                            </FormLabel>
                            <Input
                                id='place'
                                isRequired={true}
                                variant='auth'
                                fontSize='sm'
                                ms={{base: "0px", md: "0px"}}
                                type='text'
                                fontWeight='500'
                                size='lg'
                                {...register('place', {
                                    required: 'Bitte geben Sie einen Ort an',
                                })}
                            />
                        </FormControl>
                    </Flex>
                    <FormControl>
                        <FormLabel
                            display='flex'
                            ms='4px'
                            fontSize='sm'
                            fontWeight='500'
                            color={textColor}
                            htmlFor='country'
                        >
                            Land<Text color={brandStars}>*</Text>
                        </FormLabel>
                        <EuropeanCountriesSelect
                            register={register('country', {required: 'Bitte geben Sie ein Land an'})}/>
                    </FormControl>
                </Card>
                <Card mb={"20px"}>
                    <Heading color={textColor} fontSize='36px' mb='10px'>
                        Rechnungsadresse
                    </Heading>
                    <FormControl display='flex' alignItems='center' mb='24px'>
                        <Checkbox
                            id='consent'
                            colorScheme='brandScheme'
                            me='10px'
                            isChecked={watch('has_invoice_address')}
                            {...register('has_invoice_address')}
                        />
                        <FormLabel
                            htmlFor='consent'
                            mb='0'
                            fontWeight='normal'
                            color={textColor}
                            fontSize='sm'>
                            Rechnungsadresse identisch
                        </FormLabel>
                    </FormControl>
                    {!watch("has_invoice_address") ? <div>
                        <FormControl mb='24px'>
                            <FormLabel
                                display='flex'
                                ms='4px'
                                fontSize='sm'
                                fontWeight='500'
                                color={textColor}
                                htmlFor='invoice_company'
                            >
                                Firma
                            </FormLabel>
                            <Input
                                id='invoice_company'
                                variant='auth'
                                fontSize='sm'
                                ms={{base: "0px", md: "0px"}}
                                type='text'
                                fontWeight='500'
                                size='lg'
                                {...register('invoice_company')}
                            />
                        </FormControl>
                        <Flex justifyContent='space-between' gap='15px' align='center' mb='24px'>
                            <FormControl>
                                <FormLabel
                                    display='flex'
                                    ms='4px'
                                    fontSize='sm'
                                    fontWeight='500'
                                    color={textColor}
                                    htmlFor='invoice_firstname'
                                >
                                    Vorname<Text color={brandStars}>*</Text>
                                </FormLabel>
                                <Input
                                    id='invoice_firstname'
                                    isRequired={true}
                                    variant='auth'
                                    fontSize='sm'
                                    ms={{base: "0px", md: "0px"}}
                                    type='text'
                                    fontWeight='500'
                                    size='lg'
                                    {...register('invoice_firstname', {
                                        required: 'Bitte gebe den Vornamen an',
                                    })}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel
                                    display='flex'
                                    ms='4px'
                                    fontSize='sm'
                                    fontWeight='500'
                                    color={textColor}
                                    htmlFor='invoice_lastname'
                                >
                                    Nachname<Text color={brandStars}>*</Text>
                                </FormLabel>
                                <Input
                                    id='invoice_lastname'
                                    isRequired={true}
                                    variant='auth'
                                    fontSize='sm'
                                    ms={{base: "0px", md: "0px"}}
                                    type='text'
                                    fontWeight='500'
                                    size='lg'
                                    {...register('invoice_lastname', {
                                        required: 'Bitte gebe den Nachnamen an',
                                    })}
                                />
                            </FormControl>
                        </Flex>
                        <Flex justifyContent='space-between' gap='15px' align='center' mb='24px'>
                            <FormControl>
                                <FormLabel
                                    display='flex'
                                    ms='4px'
                                    fontSize='sm'
                                    fontWeight='500'
                                    color={textColor}
                                    htmlFor='invoice_street'
                                >
                                    Strasse<Text color={brandStars}>*</Text>
                                </FormLabel>
                                <Input
                                    id='invoice_street'
                                    isRequired={true}
                                    variant='auth'
                                    fontSize='sm'
                                    ms={{base: "0px", md: "0px"}}
                                    type='text'
                                    fontWeight='500'
                                    size='lg'
                                    {...register('invoice_street', {
                                        required: 'Bitte geben Sie eine Strasse an',
                                    })}
                                />
                            </FormControl>
                            <FormControl width='25%'>
                                <FormLabel
                                    display='flex'
                                    ms='4px'
                                    fontSize='sm'
                                    fontWeight='500'
                                    color={textColor}
                                    htmlFor='invoice_street_nr'
                                >
                                    Nummer<Text color={brandStars}>*</Text>
                                </FormLabel>
                                <Input
                                    id='invoice_street_nr'
                                    isRequired={true}
                                    variant='auth'
                                    fontSize='sm'
                                    ms={{base: "0px", md: "0px"}}
                                    type='text'
                                    fontWeight='500'
                                    size='lg'
                                    {...register('invoice_street_nr', {
                                        required: 'Bitte geben Sie eine Strassennummer an',
                                    })}
                                />
                            </FormControl>
                        </Flex>
                        <Flex justifyContent='space-between' gap='15px' align='center' mb='24px'>
                            <FormControl width='25%'>
                                <FormLabel
                                    display='flex'
                                    ms='4px'
                                    fontSize='sm'
                                    fontWeight='500'
                                    color={textColor}
                                    htmlFor='invoice_zip'
                                >
                                    Postleitzahl<Text color={brandStars}>*</Text>
                                </FormLabel>
                                <Input
                                    id='invoice_zip'
                                    isRequired={true}
                                    variant='auth'
                                    fontSize='sm'
                                    ms={{base: "0px", md: "0px"}}
                                    type='text'
                                    fontWeight='500'
                                    size='lg'
                                    {...register('invoice_zip', {
                                        required: 'Bitte geben Sie eine Postleitzahl an',
                                    })}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel
                                    display='flex'
                                    ms='4px'
                                    fontSize='sm'
                                    fontWeight='500'
                                    color={textColor}
                                    htmlFor='invoice_place'
                                >
                                    Ort<Text color={brandStars}>*</Text>
                                </FormLabel>
                                <Input
                                    id='invoice_place'
                                    isRequired={true}
                                    variant='auth'
                                    fontSize='sm'
                                    ms={{base: "0px", md: "0px"}}
                                    type='text'
                                    fontWeight='500'
                                    size='lg'
                                    {...register('invoice_place', {
                                        required: 'Bitte geben Sie einen Ort an',
                                    })}
                                />
                            </FormControl>
                        </Flex>
                        <FormControl>
                            <FormLabel
                                display='flex'
                                ms='4px'
                                fontSize='sm'
                                fontWeight='500'
                                color={textColor}
                                htmlFor='invoice_country'
                            >
                                Land<Text color={brandStars}>*</Text>
                            </FormLabel>
                            <EuropeanCountriesSelect
                                register={register('invoice_country', {required: 'Bitte geben Sie ein Land an'})}/>
                        </FormControl>
                    </div> : ''}
                </Card>

                <Card>
                    <Heading color={textColor} fontSize='36px' mb='10px'>
                        Weitere Angaben
                    </Heading>
                    <FormControl>
                        <FormLabel
                            display='flex'
                            ms='4px'
                            fontSize='sm'
                            fontWeight='500'
                            color={textColor}
                            htmlFor='vatid'
                        >
                            Umsatzsteuer-ID
                        </FormLabel>
                        <Input
                            id='vatid'
                            variant='auth'
                            fontSize='sm'
                            type='text'
                            fontWeight='500'
                            size='lg'
                            {...register('vatid')}
                        />
                    </FormControl>
                </Card>

                <Button
                    fontSize='sm'
                    variant='brand'
                    fontWeight='500'
                    w='100%'
                    h='50'
                    mt='24px'
                    mb='24px'
                    isLoading={isSubmitting}
                    type='submit'
                >
                    Änderungen speichern
                </Button>
            </form>
        </Box>
    )
        ;
}
